import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CardLayout } from '../../lib/card';
import { MediaImage, MediaVideo } from '../components';
import { usePost } from '../Post';
import {
  CardActionsBlock,
  CardContainer,
  CardMediaBlock,
  CardMediaTypeLayer,
  CardPreviewLinkBlock,
  CardRepostPreviewBlock,
  CardTextBlock,
  CardUserBlock
} from '../parts';
import { usePostAction, usePostSettings } from '../utils';

interface Card2Props {
  space: number;
}

export const Card2 = forwardRef<HTMLDivElement, Card2Props>(
  ({ space, ...forwardingProps }, forwardingRef) => {
    const { text, showMedia, showTextLink } = usePost();
    const { backgroundColor, borderRadius, overlayColor, showShadow, shadow } =
      usePostSettings();
    const handleAction = usePostAction();

    const [isDropDownOpen, setIsDropDownOpen] = useState(false);

    return (
      <Card2Container
        {...forwardingProps}
        ref={forwardingRef}
        onClick={handleAction}
        _overlay
        _space={space}
        _isOverlayShown={isDropDownOpen}
        _backgroundColor={showMedia ? undefined : backgroundColor}
        _borderRadius={borderRadius}
        _minHeight={180}
        _shadow={showShadow ? shadow : undefined}
      >
        {showMedia ? (
          <>
            <CardMediaBlock thumbnail hideMediaType _grow />
            <CardMediaTypeLayer />

            <BackdropLayer
              _overlayColor={overlayColor}
              _isOverlayShown={isDropDownOpen}
            />

            <ContentLayer _withAnimation _isOverlayShown={isDropDownOpen}>
              <CardTextBlock disableControl grow />
              {!text && <CardLayout.Block />}
              <CardUserBlock withSource />
              <CardActionsBlock
                withTopDivider
                setIsDropDownOpen={setIsDropDownOpen}
              />
            </ContentLayer>
          </>
        ) : (
          <>
            <CardTextBlock grow />
            <CardRepostPreviewBlock />
            {showTextLink && <CardPreviewLinkBlock />}
            <CardUserBlock withSource sourceAlign="flex-start" />
            <CardActionsBlock
              withTopDivider
              setIsDropDownOpen={setIsDropDownOpen}
            />
          </>
        )}
      </Card2Container>
    );
  }
);

Card2.displayName = 'Card2';

const ContentLayer = styled(CardLayout.HiddenLayer)`
  justify-content: flex-end;
`;

const BackdropLayer = styled(CardLayout.HiddenLayer)<{
  _overlayColor?: string;
}>`
  ${({ _overlayColor }) => !!_overlayColor && `background: ${_overlayColor}`};
`;

const Card2Container = styled(CardContainer)<{
  _isOverlayShown: boolean;
}>`
  & ${MediaImage}, & ${MediaVideo} {
    transition: filter 0.3s;
  }

  &:hover ${MediaImage}, &:hover ${MediaVideo} {
    filter: grayscale(1);
  }

  ${({ _isOverlayShown }) => {
    if (_isOverlayShown) {
      return css`
        & ${MediaImage}, & ${MediaVideo} {
          filter: grayscale(1);
        }
      `;
    }
    return;
  }};
`;
